// Someone else's conversation, read-only — the org admin's "All" view in Chat.
//
// Org admins can read what their users asked and what the agents answered: a
// compliance decision the organisation made, so the page says plainly whose
// chat this is rather than dressing it up as the admin's own. There is no
// composer. Per-run cost and tool detail stay in Audit logs.
//
// The server refuses anyone but an org admin (`/my/chat/org/*`); the redirect
// below only spares an engineer a 403 from a pasted link.

import { useQuery } from "@tanstack/react-query";
import { Bot, Eye } from "lucide-react";
import * as React from "react";

import { Markdown } from "@/components/chat/markdown";
import { Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { getOrgConversation } from "@/lib/api/chat";
import { useIsOrgAdmin } from "@/lib/access";
import { useParams, useRouter } from "@/lib/navigation";
import { useMe } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

/** `/chat/org` with nothing selected yet. */
export function OrgConversationsHome() {
  useOrgAdminOnly();
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-sm text-center text-sm text-muted-foreground">
        <Eye className="mx-auto mb-3 h-6 w-6" />
        Every chat in your organisation is listed on the left. Pick one to read it. Only org admins
        can see other people&apos;s chats.
      </div>
    </div>
  );
}

export default function OrgConversationPage() {
  useOrgAdminOnly();
  const id = String(useParams().id);
  const q = useQuery({ queryKey: qk.orgConversation(id), queryFn: () => getOrgConversation(id) });

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : q.isLoading || !q.data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="border-b border-border pb-3">
              <div className="font-medium">{q.data.title}</div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Eye className="h-3.5 w-3.5" />
                Read-only · {q.data.user_email} with {q.data.agent_name}
                {q.data.is_evaluation ? " (evaluation session)" : ""}
              </div>
            </div>
            {q.data.messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-muted px-4 py-2 text-sm">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex gap-2">
                  <Bot className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  {m.status === "complete" ? (
                    <Markdown text={m.content} />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {m.status === "pending" ? "Reply in progress…" : m.content}
                    </span>
                  )}
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Send anyone but an org admin back to their own chats. */
function useOrgAdminOnly() {
  const router = useRouter();
  const { data: me } = useMe();
  const isAdmin = useIsOrgAdmin();
  React.useEffect(() => {
    if (me && !isAdmin) router.replace("/chat");
  }, [me, isAdmin, router]);
}
